import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { parseErrorDetail } from "../lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const pwChangedBanner = location.state?.pwChanged === true;
  const pwResetBanner = location.state?.pwReset === true;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const r = await login(username.trim(), password);
      if (r.mfa_required) nav("/mfa", { replace: true });
      else if (r.must_change_password) nav("/change-password", { replace: true });
      else if (r.user?.role === "OWNER_ADMIN") nav("/admin", { replace: true });
      else nav("/portal", { replace: true });
    } catch (err) {
      setError(parseErrorDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="apwo-auth-shell">
      <div className="apwo-auth-container" data-testid="auth-container">
        <div className="apwo-auth-card" role="dialog" aria-labelledby="apwo-login-title">
          <header className="apwo-auth-header">
            <p className="apwo-brand-eyebrow" data-testid="brand-eyebrow">AUTOPARTSB2B</p>
            <h1 id="apwo-login-title" className="apwo-auth-title" data-testid="login-title">AP Warehouse Ordering Portal</h1>
            <p className="apwo-auth-sub">Private ordering portal for authorised AP Warehouse personnel.</p>
          </header>

          <form className="apwo-auth-form" onSubmit={onSubmit} noValidate data-testid="login-form">
            {pwChangedBanner && (
              <div className="apwo-alert-success" role="status" data-testid="login-pw-changed-banner">
                Your password was changed. Please sign in with your new password.
              </div>
            )}
            {pwResetBanner && (
              <div className="apwo-alert-success" role="status" data-testid="login-pw-reset-banner">
                Your password has been reset. Please sign in with your new password.
              </div>
            )}
            <label className="apwo-field">
              <span>Username</span>
              <input
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                data-testid="login-username"
                autoCapitalize="none"
                autoCorrect="off"
              />
            </label>

            <label className="apwo-field">
              <span>Password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
              />
            </label>

            <p className="apwo-forgot-line" data-testid="login-forgot-line">
              <Link to="/forgot-password" className="apwo-forgot-link" data-testid="login-forgot-link">
                Forgot password?
              </Link>
            </p>

            {error && (
              <div className="apwo-alert-error" role="alert" data-testid="login-error">{error}</div>
            )}

            <button
              type="submit"
              className="apwo-btn-primary"
              disabled={submitting || !username || !password}
              data-testid="login-submit"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <footer className="apwo-auth-footer" data-testid="login-support">
            <p className="apwo-support-title">Need access or help?</p>
            <p className="apwo-support-line">
              Contact <span className="apwo-nowrap">AUTOPARTSB2B</span> on <a href="tel:+61426266655" data-testid="login-support-phone">0426 266 655</a>
              {" "}or <a href="mailto:order@autopartsb2b.com.au" data-testid="login-support-email">order@autopartsb2b.com.au</a>.
            </p>
            <p className="apwo-fineprint" data-testid="login-fineprint">
              Forgot your password? Reset it securely by email.
            </p>
          </footer>
        </div>

        <aside className="apwo-auth-sidepanel" data-testid="auth-sidepanel">
          <div className="apwo-sidepanel-inner">
            <h2 data-testid="auth-hero-headline">
              Order stock, choose payment terms and track Rewards in one place.
            </h2>
            <ul>
              <li>Current 20L catalogue and tier pricing.</li>
              <li>Choose COD or Prepay at checkout.</li>
              <li>Eligible Prepay orders earn 1.5% Reward after cleared payment is confirmed.</li>
            </ul>
            <p className="apwo-sidepanel-seller">
              S1 Asia Pacific Traders Pty Ltd &middot; trading as AUTOPARTSB2B &middot; ABN 69 650 366 091
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
